import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  Hidden,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";

import parse from "html-react-parser";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";

import {
  Audiotrack as AudiotrackIcon,
  BookmarkRounded as BookmarkRoundedIcon,
  PlayArrow as PlayArrowIcon,
} from "@material-ui/icons";
import { ReactComponent as BookmarkBorderRoundedIcon } from "../../images/icons/bookmark.svg";
import { ReactComponent as DocumentIcon } from "../../images/icons/document.svg";
import VideoTagButton from "../../components/VideoTagButton";
import VideoCollectionDialog from "../../components/VideoCollectionDialog";
import LoginAlertDialog from "../../components/LoginAlertDialog";
import VideoQuotaExceedDialog from "../../components/VideoQuotaExceedDialog";

// context
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { useVideoDispatch } from "../../context/VideoContext";
import { useUserState } from "../../context/UserContext";
import { handlePlayMovie, handlePlayTrailer } from "../../context/VideoContext";

import { useVideoCollections } from "../../useGlobal";
import { reserveQuota, downloadAttachment } from "../../clientAPI";

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      margin: "6.5%",
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(3),
    },
    title: {
      ...theme.typography.h2,
      fontWeight: 700,
      color: "black",
      [theme.breakpoints.down("sm")]: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
      },
    },
    interactionButton: {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      [theme.breakpoints.down("sm")]: {
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.light,
      },
    },
    interactionButtonText: {
      ...theme.typography.h6,
      color: "black",
    },
    tag: {
      marginRight: theme.spacing(1),
      paddingLeft: 14,
      paddingRight: 14,
      borderRadius: theme.spacing(2),
      backgroundColor: "#e0e0e0",
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
      },
    },
    videoTagContainer: {
      marginTop: theme.spacing(1.25),
      marginBottom: theme.spacing(0.5),
      [theme.breakpoints.down("sm")]: {
        marginBottom: theme.spacing(0),
      },
    },
    content: {
      marginTop: theme.spacing(2.5 - 1),
    },
    info: {
      marginTop: theme.spacing(1),
    },
    subtitle: {
      color: theme.palette.common.grey[800],
    },
    colorBlack: {
      color: "black",
    },
    body: {
      color: theme.palette.common.grey[800],
    },
    dropDownList: {
      boxShadow: "0 1px 5px 1px rgba(116, 116, 116, 0.25)",
    },
    menuListItem: {
      ...theme.typography.body2,
      padding: "6px 24px 6px 24px",
      color: theme.palette.common.grey[600],
      "&:hover, &:focus, &$selected, &$selected:hover, &$selected:focus, &$menuListItemSelected, &$menuListItemSelected:focus, &$menuListItemSelected:hover": {
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: "white",
      },
    },
    menuListItemSelected: {},
    dialogPaper: {
      padding: theme.spacing(1),
    },
    dialogTitle: {
      ...theme.typography.subtitle1,
      fontWeight: 500,
    },
    dialogDescription: {
      ...theme.typography.smallCaption,
    },
    dialogButton: {
      ...theme.typography.caption,
      fontWeight: 700,
    },
    grow: {
      flexGrow: 1,
    },
  }),
);


export default function MovieDescription ({
  videoId,
  videoInfo,
  videoType,
  trackIndex,
  playerRef,
}) {
  const classes = useStyles();
  const history = useHistory();

  const { trackEvent } = useMatomo();
  const videoDispatch = useVideoDispatch();
  const { isAuthenticated, userInfo } = useUserState();

  const isLogin = isAuthenticated;
  const isTrailer = videoType === "trailer";

  const {
    video = {},
    directors = [],
    actors = [],
    tags = [],
    attachments = [],
  } = videoInfo;
  const { videoCollections } = useVideoCollections(video.id, isAuthenticated);
  const { collections = [] } = videoCollections;
  const status = collections.map(e => ({
    ...e,
    cid: e.id,
  }));
  const checkedIds = status.filter(e => e.collected).map(e => e.id);
  const bookmarkState = isAuthenticated && checkedIds.length > 0;

  const [openBookmarkDialog, setOpenBookmarkDialog] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openQuotaDialog, setOpenQuotaDialog] = useState(false);

  const trackLangs = get(videoInfo, `lang.movie.track`);
  const subtitleLangs = get(videoInfo, `lang.movie.subtitle`);
  const rateName = get(videoInfo, "rate_name", "");
  const runtime = get(video, "runtime") ? `${get(video, "runtime")} 分鐘` : "";
  const tracks = get(videoInfo, `play.${videoType}`);

  const metaInfo = [trackLangs, subtitleLangs, rateName, runtime]
    .filter(e => !isEmpty(e))
    .join(" | ");

  const requireLoginWrapper = cb => {
    if (isAuthenticated) {
      return cb;
    } else {
      return () => {
        handleLoginDialogOpen();
      };
    }
  };

  const handleLoginDialogOpen = () => {
    setOpenLoginDialog(true);
  };

  const handleBookmarkDialogOpen = () => {
    setOpenBookmarkDialog(true);
  };

  const handleBookmarkDialogClose = () => {
    setOpenBookmarkDialog(false);
  };

  const handleBookmarkClick = requireLoginWrapper(() =>
    handleBookmarkDialogOpen(true),
  );

  const handleClickPlayMovie = () => {
    handlePlayMovie({
      isLogin,
      isTrailer,
      videoId,
      history,
      reserveQuota,
      setOpenLoginDialog,
      setOpenQuotaDialog,
      player: playerRef.current,
      trackEvent,
    });
  };

  const handleClickPlayTrailer = () => {
    handlePlayTrailer({
      isLogin,
      isTrailer,
      videoId,
      history,
      reserveQuota,
      setOpenLoginDialog,
      setOpenQuotaDialog,
      player: playerRef.current,
      trackEvent,
    });
  };

  const PlayMovieButton = () => {
    return (
      <Button
        className={classes.interactionButton}
        startIcon={<PlayArrowIcon />}
        onClick={handleClickPlayMovie}
      >
        <Box className={classes.interactionButtonText}>播放正片</Box>
      </Button>
    );
  };

  const PlayTrailerButton = () => {
    return (
      <Button
        className={classes.interactionButton}
        startIcon={<PlayArrowIcon />}
        onClick={handleClickPlayTrailer}
      >
        <Box className={classes.interactionButtonText}>播放預告</Box>
      </Button>
    );
  };

  const BookmarkButton = () => {
    return (
      <Button
        className={classes.interactionButton}
        startIcon={
          bookmarkState ? (
            <BookmarkRoundedIcon />
          ) : (
            <BookmarkBorderRoundedIcon />
          )
        }
        onClick={handleBookmarkClick}
      >
        <Box className={classes.interactionButtonText}>收藏影片</Box>
      </Button>
    );
  };

  const ChangeTrackButton = () => {
    const [trackMenuAnchorEl, setTrackMenuAnchorEl] = useState(null);

    const handleClickTrackButton = event => {
      if (!isTrailer && !isLogin) {
        setOpenLoginDialog(true);
      } else {
        setTrackMenuAnchorEl(event.currentTarget);
      }
    };
    const handleTrackMenuClose = () => {
      setTrackMenuAnchorEl(null);
    };

    const handleClickTrackItem = idx => {
      if (!isTrailer && !isLogin) {
        setOpenLoginDialog(true);
      } else {
        if (isUndefined(idx) || isNull(idx)) return;
        const nextTrack = get(tracks, idx);
        if (nextTrack) {
          videoDispatch({
            type: "UPDATE_VOICE_TRACK_SUCCESS",
            payload: { url: nextTrack.url, trackIndex: idx },
          });
        }
      }
      handleTrackMenuClose();
    };

    if (!(tracks && tracks.length > 0)) {
      return null;
    }

    return (
      <>
        <Button
          className={classes.interactionButton}
          startIcon={<AudiotrackIcon />}
          aria-controls='track-menu'
          aria-haspopup='true'
          onClick={handleClickTrackButton}
        >
          <Box className={classes.interactionButtonText}>選擇配音</Box>
        </Button>
        <Menu
          id='track-menu'
          classes={{ paper: classes.dropDownList }}
          anchorEl={trackMenuAnchorEl}
          getContentAnchorEl={null}
          keepMounted
          elevation={0}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          autoFocus={false}
          open={Boolean(trackMenuAnchorEl)}
          onClose={handleTrackMenuClose}
        >
          {tracks.map((track, idx) => (
            <MenuItem
              key={track.id}
              classes={{
                root: classes.menuListItem,
                selected: classes.menuListItemSelected,
              }}
              selected={idx === trackIndex}
              onClick={() => {
                handleClickTrackItem(idx);
              }}
            >
              {track.langName}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  const AttachmentButton = () => {
    const [attachmentMenuAnchorEl, setAttachmentMenuAnchorEl] = useState(null);
    const handleClickAttachmentButton = requireLoginWrapper(event => {
      setAttachmentMenuAnchorEl(event.currentTarget);
    });

    const handleAttachmentMenuClose = () => {
      setAttachmentMenuAnchorEl(null);
    };

    const handleClickAttchementItem = requireLoginWrapper(id => {
      if (isEmpty(id)) return;
      downloadAttachment(id).finally(() => handleAttachmentMenuClose());
    });
    return (
      <>
        <Button
          className={classes.interactionButton}
          startIcon={<DocumentIcon />}
          aria-controls='attachment-menu'
          aria-haspopup='true'
          onClick={handleClickAttachmentButton}
        >
          <Box className={classes.interactionButtonText}>下載學習單</Box>
        </Button>
        <Menu
          id='attachment-menu'
          classes={{ paper: classes.dropDownList }}
          anchorEl={attachmentMenuAnchorEl}
          getContentAnchorEl={null}
          keepMounted
          elevation={0}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          autoFocus={false}
          open={Boolean(attachmentMenuAnchorEl)}
          onClose={handleAttachmentMenuClose}
        >
          {attachments.map(attachment => (
            <MenuItem
              key={attachment.id}
              className={classes.menuListItem}
              onClick={() => {
                handleClickAttchementItem(attachment.id);
              }}
            >
              {attachment.name}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  const InteractionButtons = () => {
    return (
      <>
        <Grid item>
          <PlayMovieButton />
        </Grid>
        <Grid item>
          <PlayTrailerButton />
        </Grid>
        <Grid item>
          <ChangeTrackButton />
        </Grid>
        {!isEmpty(attachments) && (
          <Grid item>
            <AttachmentButton />
          </Grid>
        )}
      </>
    );
  };

  const DefaultHeadLayout = () => {
    return (
      <Grid item xs={12}>
        <Grid container justify='flex-start' alignItems='baseline'>
          <Grid item>
            <Box className={classes.title}>{video.title_zh}</Box>
          </Grid>
          <Grid item className={classes.grow}></Grid>
          <InteractionButtons />
        </Grid>
      </Grid>
    );
  };

  const SmallHeadLayout = () => {
    return (
      <>
        <Grid item xs={12}>
          <Grid container justify='flex-start' alignItems='baseline'>
            <InteractionButtons />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box className={classes.title}>{video.title_zh}</Box>
        </Grid>
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        <Hidden xsDown>
          <DefaultHeadLayout />
        </Hidden>
        <Hidden smUp>
          <SmallHeadLayout />
        </Hidden>
        <Grid item xs={12}>
          <Grid container>
            <Grid item>
              <Typography variant='h6' className={classes.subtitle}>
                {metaInfo}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant='h6' className={classes.subtitle}>
            {video.title_fl}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container className={classes.videoTagContainer}>
            {tags.map(tag => (
              <Grid item key={tag.name}>
                <VideoTagButton
                  name={tag.name}
                  href={`/video/list?tag_name=${tag.name}&expand_tag=true`}
                ></VideoTagButton>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <div className={classes.content}>
          <Grid item xs={12}>
            <Typography variant='h6' className={classes.colorBlack}>
              劇情介紹
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.info}>
            <div className={classes.body}>{parse(video.description)}</div>
          </Grid>

          <Grid item xs={12} className={classes.info}>
            <Grid container spacing={1}>
              <Grid item>
                <Typography variant='h6' className={classes.colorBlack}>
                  導演
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant='h5' className={classes.body}>
                  {directors.map(d => d.name_zh || d.name_fl).join("、")}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.info}>
            <Grid container spacing={1}>
              <Grid item>
                <Typography variant='h6' className={classes.colorBlack}>
                  演員
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant='h5' className={classes.body}>
                  {actors.map(d => d.name_zh || d.name_fl).join("、")}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <div>
        <LoginAlertDialog open={openLoginDialog} setOpen={setOpenLoginDialog} />
        <VideoQuotaExceedDialog
          open={openQuotaDialog}
          setOpen={setOpenQuotaDialog}
        />
        <VideoCollectionDialog
          videoId={video.id}
          open={openBookmarkDialog}
          onClose={handleBookmarkDialogClose}
          status={status}
          checkedIds={checkedIds}
        />
      </div>
    </div>
  );
}
